import React from "react";
import { useForm, yupResolver } from "@mantine/form";
import {
  Button,
  Group,
  Modal,
  Text,
  Select,
  Space,
  Textarea,
} from "@mantine/core";

import Colors from "../../../themes/Colors";
import { QueryResolution } from "../service/model/QueryInbox";
import QueryValidationSchema from "../service/validator/QueryValidationSchema";

type ConfirmResolveTicketModalProps = {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  resolveTicket: (queryOutcome?: string, employeeMessage?: string) => void;
  queryOutcomeOptions: { value: string; label: string }[];
  queryResolutionDataEnabled: boolean;
  values?: QueryResolution;
  lang: {
    title: string;
    description: string;
    confirm: string;
    cancel: string;
    queryOutcome: string;
    employeeMessage: string;
  };
};

export const ConfirmResolveTicketModal: React.FC<
  ConfirmResolveTicketModalProps
> = ({
  isOpen,
  setOpen,
  resolveTicket,
  queryOutcomeOptions,
  queryResolutionDataEnabled,
  values,
  lang,
}) => {
  const form = useForm({
    schema: yupResolver(QueryValidationSchema),
    initialValues: {
      queryOutcome: values?.queryOutcome ?? "",
      employeeMessage: values?.employeeMessage ?? "",
    },
  });

  const onSubmitHandler = (formValues: typeof form.values): void => {
    if (queryResolutionDataEnabled) {
      const { queryOutcome, employeeMessage } = formValues;
      resolveTicket(queryOutcome, employeeMessage);
    } else {
      resolveTicket();
    }

    setOpen(false);
  };

  return (
    <form
      id="resolve-query-form"
      onSubmit={form.onSubmit((formValues) => onSubmitHandler(formValues))}
      style={{ width: "100%" }}
    >
      <Modal
        opened={isOpen}
        onClose={() => {
          setOpen(false);
        }}
        size="lg"
        withCloseButton={false}
        closeOnClickOutside={false}
        centered
      >
        <div>
          <Text
            size="xl"
            style={{
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: "24.59px",
            }}
          >
            {lang.title}
          </Text>
          <Text size="xs" weight={400} mt={16}>
            {lang.description}
          </Text>

          {queryResolutionDataEnabled && (
            <>
              <Space h="md" />
              <div>
                <Select
                  required
                  id="queryOutcome"
                  label={lang.queryOutcome}
                  data={queryOutcomeOptions}
                  placeholder="Select a reason"
                  value={form.values.queryOutcome?.toString() ?? null}
                  onChange={(value) =>
                    form.setFieldValue("queryOutcome", value as string)
                  }
                  dropdownPosition="bottom"
                  maxDropdownHeight={400} // Adjust this value as needed to show all options without scrolling
                />
                <span style={{ color: "red", fontSize: "14px" }}>
                  {form.errors.queryOutcome}
                </span>
              </div>
              <Space h="md" />
              <div>
                <Textarea
                  required
                  id="employeeMesssage"
                  label={lang.employeeMessage}
                  placeholder="Type your message here"
                  value={form.values.employeeMessage ?? null}
                  onChange={(event) =>
                    form.setFieldValue(
                      "employeeMessage",
                      event.currentTarget.value
                    )
                  }
                  radius="md"
                  minRows={3}
                  maxLength={300}
                />
                <span style={{ color: "red", fontSize: "14px" }}>
                  {form.errors.employeeMessage}
                </span>
                {!form.errors.employeeMessage && (
                  <Text
                    color={
                      form.values.employeeMessage.length !== 300
                        ? Colors.description.primary
                        : Colors.description.error
                    }
                    style={{
                      width: "auto",
                      float: "right",
                      fontSize: "12px",
                    }}
                  >
                    {form.values.employeeMessage.length}/300 characters
                  </Text>
                )}
              </div>
            </>
          )}
          <Space h="xl" />
          <Group spacing="xl" mt={16} position="right">
            <Button
              variant="outline"
              style={{
                height: "40px",
                padding: "0 20px",
                width: "150px",
              }}
              onClick={() => setOpen(false)}
            >
              {lang.cancel}
            </Button>

            <Button
              type="submit"
              form="resolve-query-form"
              onClick={() => {
                // Only called when the form fields are disbaled by the "queryResolutionDataEnabled" feature.
                if (!queryResolutionDataEnabled) {
                  onSubmitHandler({ employeeMessage: "", queryOutcome: "" });
                }
              }}
              style={{
                height: "40px",
                padding: "0 20px",
                width: "150px",
              }}
            >
              {lang.confirm}
            </Button>
          </Group>
        </div>
      </Modal>
    </form>
  );
};
